import { DOCUMENT } from '@angular/common';
import { Injectable, inject, provideEnvironmentInitializer } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  GuardsCheckEnd,
  NavigationEnd,
  NavigationStart,
  Router,
  ViewTransitionInfo,
  ViewTransitionsFeature,
  withViewTransitions,
} from '@angular/router';

import { Point, smoothOpenPath } from '../utils/liquid-path.utils';
import { MotionPreferenceService } from './motion-preference.service';

// ─── Rythme ──────────────────────────────────────────────────────────────────
/** Durée du balayage : assez pour être lu comme un geste, assez court pour ne jamais faire attendre. */
const DURATION = 440;
/** Échantillonnage des images clés du masque (WAAPI interpole entre elles). */
const FRAME_STEP = 1000 / 60;
/** Points le long du front : bords souples, tracé léger (deux masques × ~27 images). */
const FRONT_POINTS = 22;
/** Débord des tracés hors de la zone, pour que l'antialiasing ne laisse jamais de filet au bord. */
const PAD = 6;
/**
 * Les masques se referment loin hors de la zone : si sa taille n'est pas tout à
 * fait celle mesurée (en-tête qui change de hauteur d'une page à l'autre), les
 * deux captures couvrent quand même tout, et le fond orange de la paire
 * d'images ne se voit jamais ailleurs que dans le liseré.
 */
const OVERFLOW = 4000;

// ─── Noms des groupes de la View Transition ──────────────────────────────────
// Doivent correspondre à styles/ui/route-transition.scss. Ils ne sont posés
// QUE pendant une transition de route (classe sur <html>) : sinon la goutte du
// thème, qui découpe ::view-transition-new(root), ne recouvrirait plus la
// sidenav, l'en-tête ni le contenu, capturés à part.
const CONTENT_NAME = 'route-content';
/** Transition animée : le front de café balaie la zone de contenu. */
const SWEEP_CLASS = 'route-sweep-transition';
/** Animations avancées coupées ou prefers-reduced-motion : fondu enchaîné bref du contenu, sans mouvement. */
const FADE_CLASS = 'route-fade-transition';
/** Repli : bascule nette, aucune animation (cas qui aurait dû être écarté en amont). */
const INSTANT_CLASS = 'route-instant-transition';
/** Posée par ThemeTransitionService pendant une bascule de thème. */
const THEME_SWITCHING_CLASS = 'theme-switching';

/** Sens du balayage : depuis la sidenav (navigation), ou vers elle (retour dans l'historique). */
type Direction = 1 | -1;

interface Wave { k: number; amp: number; phase: number; speed: number; }

/** Tout ce qui ne dépend que de la navigation : calculé une fois, rejoué à chaque image. */
interface Sweep {
  /** Taille de la zone de contenu : repère local des pseudo-éléments ::view-transition-*(route-content). */
  w: number;
  h: number;
  dir: Direction;
  waves: Wave[];
  /** Ménisque : le centre du front devance légèrement les bords, comme un liquide qu'on pousse. */
  bulge: number;
  /** Abscisses de départ et d'arrivée du front, débords d'ondulation compris. */
  from: number;
  to: number;
}

/**
 * Easing du balayage, cubic-bezier(0.55, 0, 0.2, 1) : départ posé, long
 * ralenti à l'arrivée, comme un liquide qui perd son élan. Calculé ici car les
 * images clés sont jouées en linéaire (l'easing doit porter sur la position du
 * front, pas sur l'interpolation entre deux tracés).
 */
const EASE = cubicBezier(0.55, 0, 0.2, 1);

function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const ax = 1 + 3 * x1 - 3 * x2, bx = 3 * x2 - 6 * x1, cx = 3 * x1;
  const ay = 1 + 3 * y1 - 3 * y2, by = 3 * y2 - 6 * y1, cy = 3 * y1;
  const sx = (u: number) => ((ax * u + bx) * u + cx) * u;
  const sy = (u: number) => ((ay * u + by) * u + cy) * u;
  return (t: number) => {
    if (t <= 0 || t >= 1) return Math.min(Math.max(t, 0), 1);
    // Bissection : 20 itérations suffisent largement (précision < 1e-6), calcul fait ~27 fois par navigation.
    let lo = 0, hi = 1, u = t;
    for (let i = 0; i < 20; i++) {
      u = (lo + hi) / 2;
      if (sx(u) < t) lo = u; else hi = u;
    }
    return sy(u);
  };
}

/**
 * Options internes de `withViewTransitions` : objet unique injecté par le
 * routeur, relu à chaque navigation. Son drapeau `skipNextTransition` est le
 * seul moyen d'empêcher Angular d'appeler `document.startViewTransition`.
 */
let routerTransitionOptions: { skipNextTransition?: boolean } | null = null;

/**
 * À passer à `provideRouter` à la place de `withViewTransitions()`.
 *
 * Branche la transition « front de café » et instancie le service dès le
 * démarrage : il doit écouter le routeur avant la première navigation qu'il
 * aura à écarter (filtres du hub, etc.).
 */
export function withCoffeeRouteTransitions(): ViewTransitionsFeature {
  const feature = withViewTransitions({
    // Premier chargement : rien à « révéler », la page arrive d'elle-même.
    skipInitialTransition: true,
    onViewTransitionCreated: info => inject(RouteTransitionService).onTransitionCreated(info),
  });
  // API interne (ɵ) : on récupère l'objet d'options tel que le routeur l'injectera.
  // S'il change de forme dans une future version d'Angular, on retombe sur la
  // bascule instantanée de onTransitionCreated : moins économe, mais correct.
  for (const p of feature.ɵproviders) {
    const value = (p as { useValue?: unknown }).useValue;
    if (value && typeof value === 'object' && 'skipNextTransition' in value) {
      routerTransitionOptions = value as { skipNextTransition?: boolean };
    }
  }
  feature.ɵproviders.push(provideEnvironmentInitializer(() => inject(RouteTransitionService)));
  return feature;
}

/**
 * Transition entre les pages « front de café ».
 *
 * La nouvelle page est révélée par un front liquide aux bords ondulants qui
 * balaie la zone de contenu depuis la sidenav (en sens inverse pour un retour
 * dans l'historique), souligné d'un filet orange.
 * Volontairement plus sobre que la goutte du thème (pas de chute, pas
 * d'éclaboussure) : elle se joue à chaque clic, elle doit rester discrète.
 *
 * Technique, même famille que ThemeTransitionService :
 * - la View Transition du routeur capture l'ancienne et la nouvelle page ;
 * - seule `.content-main` change : la sidenav, l'en-tête et les calques
 *   flottants (menus, toasts, panneau de droite) reçoivent leur propre
 *   `view-transition-name`, sans animation — ils ne bougent pas ;
 * - le front est précalculé en images clés `clip-path: path()` (une par
 *   frame à 60 fps) et confié à WAAPI. Deux masques complémentaires : la
 *   nouvelle page à gauche du front, l'ancienne à droite du front décalé de
 *   quelques pixels. L'interstice laisse voir le fond orange de la paire
 *   d'images : c'est le liseré, sans calque DOM ni requestAnimationFrame,
 *   donc toujours calé au pixel près sur le front.
 *
 * Pas de transition (et pas de `startViewTransition` du tout, ce qui supprime
 * les « InvalidStateError: Transition was aborted » des navigations qui se
 * chevauchent) quand seuls les query params ou le fragment changent, quand le
 * chemin est identique, au premier chargement, onglet masqué, ou pendant une
 * bascule de thème.
 */
@Injectable({ providedIn: 'root' })
export class RouteTransitionService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly motion = inject(MotionPreferenceService);

  /** Vrai si c'est NOUS qui avons levé `skipNextTransition` (à ne pas confondre avec skipInitialTransition). */
  private armed = false;
  /** Navigation en cours lancée par précédent / suivant, et identifiant de l'entrée d'historique visée. */
  private popstateTarget: number | null = null;
  /** Identifiant (navigationId Angular) de l'entrée d'historique affichée. */
  private currentEntry = 0;

  private runId = 0;
  /**
   * Une transition de route a été créée mais n'est pas encore prête (captures
   * en cours, page en train de se rendre). En lancer une autre à ce moment
   * l'interromprait avec un rejet de `ready` (« Transition was aborted ») :
   * c'est le cas des redirections en cascade (/calendar → /calendar/v/absences).
   */
  private pending = false;
  /** Animations du masque en cours, annulées en fin de transition. */
  private masks: Animation[] = [];

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Une navigation plus récente remplace la précédente : le drapeau qu'on avait
        // levé pour celle-ci (peut-être annulée avant d'atteindre l'activation) ne vaut plus.
        if (this.armed && routerTransitionOptions) routerTransitionOptions.skipNextTransition = false;
        this.armed = false;
        this.popstateTarget = event.navigationTrigger === 'popstate'
          ? event.restoredState?.navigationId ?? null
          : null;
      } else if (event instanceof GuardsCheckEnd) {
        // La cible est connue (redirections comprises), l'état courant n'a pas encore
        // changé. Pas ResolveEnd : il n'est pas émis quand aucune route n'est
        // (ré)activée — précisément le cas des navigations qui ne changent que les query params.
        // Si une transition est encore en préparation, la navigation suivante (redirection)
        // se fait sans la sienne : son rendu rejoint la capture ou le balayage déjà lancés.
        if (routerTransitionOptions && (this.pending || !this.shouldAnimate(this.router.routerState.snapshot.root, event.state.root))) {
          routerTransitionOptions.skipNextTransition = true;
          this.armed = true;
        }
      } else if (event instanceof NavigationEnd) {
        const state = this.document.defaultView?.history.state as { navigationId?: number } | null;
        this.currentEntry = state?.navigationId ?? event.id;
      }
    });
  }

  /** Appelé par le routeur juste après `startViewTransition`, avant la capture de l'ancienne page. */
  onTransitionCreated({ transition, from, to }: ViewTransitionInfo): void {
    // Rejets attendus (transition sautée, ou interrompue par une autre) : rien à signaler.
    transition.ready.catch(() => undefined);
    transition.finished.catch(() => undefined);

    const root = this.document.documentElement;
    const content = this.contentArea();
    // Animations avancées coupées (bouton de l'en-tête) ou mouvement réduit système : fondu bref.
    const reducedMotion = !this.motion.richMotion();
    // Filet de sécurité si le drapeau du routeur n'a pas pu être levé à temps :
    // bascule instantanée plutôt que skipTransition(), dont le rejet de `ready`
    // serait journalisé par le routeur en mode dev (« AbortError: Transition was skipped »).
    const instant = !content || !this.shouldAnimate(from, to);

    // Pas besoin d'interrompre un balayage en cours : startViewTransition() vient de le faire.
    for (const a of this.masks) a.cancel();
    this.masks = [];
    const id = ++this.runId;
    const modeClass = instant ? INSTANT_CLASS : reducedMotion ? FADE_CLASS : SWEEP_CLASS;
    // Posée maintenant, la classe est là pour les deux captures : les noms de groupe existent avant et après.
    root.classList.add(modeClass);
    this.pending = true;

    // Sens décidé maintenant : à `ready`, NavigationEnd a déjà mis à jour l'entrée courante.
    const dir: Direction = this.popstateTarget !== null && this.popstateTarget < this.currentEntry ? -1 : 1;
    transition.ready
      .then(() => {
        if (modeClass !== SWEEP_CLASS || id !== this.runId) return;
        // Géométrie mesurée sur la NOUVELLE page : les pseudo-éléments prennent sa taille,
        // et la hauteur de la zone varie avec l'en-tête (jusqu'à ~60 px d'une page à l'autre).
        // Mesurée sur l'ancienne, une bande du bas n'était couverte par aucune des deux
        // captures et le fond orange du liseré s'y montrait en grand rectangle.
        // Plus de zone de contenu (page sans coque) : pas de balayage, l'ancienne capture reste pleine.
        const area = this.contentArea();
        if (!area) return;
        const sweep = this.buildSweep(area, dir);
        const timing: KeyframeAnimationOptions = { duration: DURATION, easing: 'linear', fill: 'both' };
        const { reveal, cover } = this.buildClipKeyframes(sweep);
        this.masks = [
          root.animate({ clipPath: reveal }, { ...timing, pseudoElement: `::view-transition-new(${CONTENT_NAME})` }),
          root.animate({ clipPath: cover }, { ...timing, pseudoElement: `::view-transition-old(${CONTENT_NAME})` }),
        ];
      })
      .catch(() => undefined)
      .finally(() => {
        if (id === this.runId) this.pending = false;
      });

    transition.finished
      .catch(() => undefined)
      .then(() => {
        // Une transition plus récente a peut-être déjà pris le relais : on ne lui retire pas son état
        // (elle peut porter la même classe que celle-ci).
        if (id !== this.runId) return;
        root.classList.remove(SWEEP_CLASS, FADE_CLASS, INSTANT_CLASS);
        // fill: 'both' garderait les animations attachées au document après la fin.
        for (const a of this.masks) a.cancel();
        this.masks = [];
        this.pending = false;
      });
  }

  // ─── Quand animer ──────────────────────────────────────────────────────────

  private shouldAnimate(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): boolean {
    const doc = this.document;
    // Premier chargement (au cas où skipInitialTransition ne suffirait pas, ex. redirection initiale).
    if (!this.router.navigated) return false;
    // Onglet masqué : le navigateur annulerait la transition avec une InvalidStateError.
    if (doc.visibilityState === 'hidden') return false;
    // Une goutte de thème est en cours : une nouvelle View Transition l'interromprait net.
    if (doc.documentElement.classList.contains(THEME_SWITCHING_CLASS)) return false;
    // Hors de la coque applicative (connexion, maintenance…) : pas de zone de contenu à balayer.
    if (!this.contentArea()) return false;
    // Même page (query params, fragment, outlet secondaire comme le panneau de droite) :
    // filtres et onglets du hub en `?vue=`, recherche avec debounce… rien à révéler.
    return this.primaryPath(from) !== this.primaryPath(to);
  }

  private contentArea(): HTMLElement | null {
    return this.document.querySelector<HTMLElement>('.layout-grid > .content-main');
  }

  /** Chemin de l'outlet principal, sans query params, fragment ni outlets nommés. */
  private primaryPath(root: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    for (let r: ActivatedRouteSnapshot | null = root; r; r = r.firstChild) {
      for (const s of r.url) segments.push(s.path);
    }
    return '/' + segments.join('/');
  }

  // ─── Géométrie ─────────────────────────────────────────────────────────────

  private buildSweep(content: HTMLElement, dir: Direction): Sweep {
    const { width: w, height: h } = content.getBoundingClientRect();
    const rand = (a: number, b: number) => a + Math.random() * (b - a);
    // Amplitude à l'échelle de la zone, bornée : une ondulation, pas une vague.
    const base = Math.min(Math.max(h * 0.014, 6), 14);
    // Fréquences (en périodes sur la hauteur) premières entre elles : pas de motif répétitif.
    const waves: Wave[] = [
      { k: 1.3, amp: 1 }, { k: 2.2, amp: 0.55 }, { k: 3.7, amp: 0.3 }, { k: 6.1, amp: 0.14 },
    ].map(l => ({ k: l.k, amp: l.amp * base, phase: rand(0, Math.PI * 2), speed: rand(0.6, 1.4) * (Math.random() < 0.5 ? -1 : 1) }));
    const ampSum = waves.reduce((s, l) => s + l.amp, 0);
    const bulge = Math.min(Math.max(w * 0.035, 18), 48);
    const band = this.bandWidth(0.5);
    return {
      w, h, dir, waves, bulge,
      // Départ : tout le front (ménisque, ondulations, liseré) est encore hors champ.
      from: -(bulge + ampSum + band + PAD),
      // Arrivée : même le creux du front a dépassé le bord opposé.
      to: w + ampSum + band + PAD,
    };
  }

  /** Épaisseur du liseré : un filet qui s'épaissit à mi-course, quand le front est le plus visible. */
  private bandWidth(s: number): number {
    return 1.5 + 2 * Math.sin(Math.PI * s);
  }

  /** Abscisses du front (repère « balayage de gauche à droite ») à la progression s ∈ [0, 1]. */
  private front(sweep: Sweep, s: number, offset: number): Point[] {
    const x0 = sweep.from + (sweep.to - sweep.from) * EASE(s);
    // Les ondulations se calment en fin de course : le liquide se pose.
    const calm = 0.55 + 0.45 * (1 - s);
    const pts: Point[] = [];
    for (let i = 0; i < FRONT_POINTS; i++) {
      const y = -PAD + (i / (FRONT_POINTS - 1)) * (sweep.h + PAD * 2);
      const v = Math.min(Math.max(y / sweep.h, 0), 1);
      let x = x0 + offset + sweep.bulge * Math.sin(Math.PI * v);
      // Chaque harmonique glisse le long du front : le bord ondule, il ne fait pas que grandir.
      for (const l of sweep.waves) x += l.amp * calm * Math.sin(l.k * v * Math.PI * 2 + l.phase + l.speed * s * Math.PI * 2);
      pts.push([x, y]);
    }
    return pts;
  }

  /**
   * Région délimitée par le front et un bord de la zone. `side` = -1 : tout ce
   * qui est derrière le front (déjà révélé) ; +1 : tout ce qui est devant.
   * Retour dans l'historique : on retourne la scène, le front part de la droite.
   * Le front est prolongé verticalement et la région refermée à OVERFLOW px
   * hors de la zone, de tous les côtés.
   */
  private region(sweep: Sweep, pts: Point[], side: -1 | 1): string {
    const mirror = (p: Point): Point => (sweep.dir === 1 ? p : [sweep.w - p[0], p[1]]);
    const edge = side === -1 ? -OVERFLOW : sweep.w + OVERFLOW;
    const front = pts.map(mirror);
    const [ex] = mirror([edge, 0]);
    const top = -OVERFLOW;
    const bottom = sweep.h + OVERFLOW;
    const f = (v: number) => v.toFixed(1);
    const first = front[0][0];
    const last = front[front.length - 1][0];
    return `${smoothOpenPath(front)}L${f(last)} ${f(bottom)}L${f(ex)} ${f(bottom)}L${f(ex)} ${f(top)}L${f(first)} ${f(top)}Z`;
  }

  /**
   * Images clés des deux masques. Le nombre de commandes reste constant d'une
   * image à l'autre : condition pour que le navigateur interpole les `path()`.
   */
  private buildClipKeyframes(sweep: Sweep): { reveal: string[]; cover: string[] } {
    const reveal: string[] = [];
    const cover: string[] = [];
    for (let t = 0; t < DURATION + FRAME_STEP; t += FRAME_STEP) {
      const s = Math.min(t / DURATION, 1);
      // Nouvelle page : derrière le front.
      reveal.push(`path('${this.region(sweep, this.front(sweep, s, 0), -1)}')`);
      // Ancienne page : devant le front décalé de l'épaisseur du liseré.
      cover.push(`path('${this.region(sweep, this.front(sweep, s, this.bandWidth(s)), 1)}')`);
    }
    return { reveal, cover };
  }
}
